import React, { useMemo, useRef } from 'react';
import { shallowEqual, useSelector } from 'umi';
import Service from '@/pages/dashboard/components/PageContainer/Service';
import { IsSingleDay, addLastDateRequestValue, formatFilterValues, formatSingleTime, otherParamsVal } from '@/pages/dashboard/utils/utils';
import { getDefaultUtcDateValue } from "@/pages/dashboard/utils/dateUtils";
import PlatformMetrics from './PlatformMetrics';
import { PERIODTIMEOBJ } from '@/pages/dashboard/_config/constant';
import { getApiData } from '@/pages/dashboard/services/common';

export default function PlatformMetricsService(props: any) {
  const { style, className, type, config, data, disableLoading = false, children, customRender = false, dataKey="keyMetricsData", ...resetProps } = props || {};
  const { cardTitleSwitchVal } = data || {};
  const timeZone = parseInt(props?.data?.time_zone || '', 10) || 0;
  // dateFilterType 是指日期筛选器类型区分，只有范围筛选、只有daily单选
  // dateFilterIsRange 是值默认进入页面日期范围删选器是否只展示单日
  const { metricsList: propsMetricsList, metricsListMap, api, haveLastDate, refreshKey = '', interval, isSalesOverview = false,
    isComplete = true, isConfirm, dateFilterType = 'rangeDateFilter',  dateFilterIsRange,
    serviceParams: restApiConfig, otherParams: otherParamsData, componentLoadingHight, formatData, formatRules,
    metricsSwitchKey = 'cardTitleSwitchVal' } = config || {};
  const { gameCode } = useSelector((state: any) => ({
    gameCode: state?.dashboard?.currentGame?.game_code, 
  }), shallowEqual);

  const metricsSwitchVal = data?.[metricsSwitchKey] || cardTitleSwitchVal;
  const metricsList = metricsListMap ? (metricsListMap?.[metricsSwitchVal] || propsMetricsList) : propsMetricsList;
  const params = useMemo(() => ({
    game_code: gameCode,
    ...(customRender ? {} : { metricsList }),
    ...props.data,
    cardTitleSwitchVal,
    metricsSwitchVal,
  }), [gameCode, cardTitleSwitchVal, metricsList, customRender, props.data, metricsSwitchVal]);

  // 请求版本号，用于解决快速切换 catalog 时的竞态问题
  const requestVersionRef = useRef<number>(0);

  const apiRequest = (params: any, data: any) => {
    // 每次发起新请求时递增版本号
    requestVersionRef.current += 1;
    const currentVersion = requestVersionRef.current;

    const metricsListData = data?.metricsList || metricsList || [];
    const list = metricsListData?.map((item: any) => {
      const data = {
        ...params,
        metrics: [item],
        trend_metrics: [item],
        breakdown_metrics: item
      };
      
      return getApiData(api, data);
    });
    return Promise.all(list).then(dataList => {
      // 版本号不匹配说明有更新的请求已发出，丢弃旧结果
      if (currentVersion !== requestVersionRef.current) {
        return new Promise(() => {});
      }
      const dataObj: any = {};
      dataList?.map((item, index) => {
        dataObj[metricsListData[index]] = item || {};
      });
      return dataObj;
    }).catch((e) => {
      // 版本号不匹配时也不触发错误处理
      if (currentVersion !== requestVersionRef.current) {
        return new Promise(() => {});
      }
      console.log(e);
    });
  };

  return (
    <div className={className} style={style}>
      <Service
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        api={apiRequest}
        data={params}
        refreshKey={refreshKey}
        interval={interval}
        dataKey={dataKey}
        formatData={formatData}
        formatRules={formatRules}
        beforeRequest={(data) => {
          const { filterVal, game_code } = data;
          const { timeType, diff, rangeTime, singleDaily, contrastDaily, timeDiff, ...filters } = filterVal || {};
          const period = PERIODTIMEOBJ?.[timeType];
          const filtersVal = formatFilterValues(filters);
          const otherParams = otherParamsVal(otherParamsData, filtersVal) || {};
          const defaultTimeRange = getDefaultUtcDateValue(timeType, rangeTime, dateFilterIsRange);
          const rangeTimeArr = diff ? defaultTimeRange : rangeTime;
          // 给一个默认值['', '']，避免rangeTime没有的时候解构报错
          const [start, end] = rangeTimeArr || ['', ''];
          const onlySingleDateFilter = dateFilterType === 'singleDaily';
          let params = {
            game_code,
            period,
            filters: filtersVal,
            start_time: start,
            end_time: end,
            card_type: '1',
            is_confirm: !diff,
            breakdown_dimension: 'os',
            ...restApiConfig,
            ...(otherParams || {})
          };
          // 开启补齐逻辑 并且非特殊页面
          if (isComplete && !isSalesOverview) {
            const single = IsSingleDay({ rangeTime, period });
            const rangeTimeArr = diff ? defaultTimeRange : formatSingleTime(rangeTime, timeType);
            const [start, end] = rangeTimeArr || ['', ''];
            params.start_time = start;
            params.end_time = end;
            // 如果是单日单周单月 都视为没点击过筛选器（用于判断指标卡片数值是否取最后一天数据）
            params.is_confirm = single ? false : !diff;
          }
          if (isSalesOverview) {
            params.is_confirm = true;
          }
          if(isConfirm){
            const single = IsSingleDay({ rangeTime, period });
            // 后端约定，lifetime 为单日筛选，卡片接口固定传is_confirm为false
            params.is_confirm = !((single || onlySingleDateFilter));
          }
          if(onlySingleDateFilter) {
            const singleTimeRange = getDefaultUtcDateValue(timeType, [singleDaily, singleDaily], false);
            params.start_time = contrastDaily? singleTimeRange?.[1] : singleTimeRange?.[0];
            params.end_time = singleTimeRange?.[1];
          }
          params = addLastDateRequestValue(params, haveLastDate, timeZone);
          return params;
        }}
        trackData={
          customRender
            ? ['game_code', 'cardTitleSwitchVal', 'metricsSwitchVal', 'by_tab']
            : ['game_code', 'metricsList', 'cardTitleSwitchVal', 'metricsSwitchVal', 'by_tab']
        }
      >
        {customRender ? (
          children
        ) : (
          <PlatformMetrics 
            {...resetProps}  
            switchData={props?.data} 
            disableLoading={disableLoading} 
            componentLoadingHight={componentLoadingHight}>
            {children}
          </PlatformMetrics>
        )}
      </Service>
    </div>
  );
};
